import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Badge,
  Tooltip
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Dashboard as DashboardIcon,
  People as UsersIcon,
  Inventory as ProductsIcon,
  Category as CategoriesIcon,
  ShoppingCart as OrdersIcon,
  LocalShipping as ShippingIcon,
  Assessment as StatsIcon,
  SupervisorAccount as AdminsIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  CardGiftcard as GiftShopIcon,
  LocalOffer as CouponsIcon,
  Warning as MissingIcon,
  Store as StoreIcon,
  ViewCarousel as BannersIcon,
  SystemUpdateAlt as UpdateIcon
} from '@mui/icons-material';
import adminAuthService from '../services/adminAuthService';
import usePWAUpdate from '../hooks/usePWAUpdate';

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboardData, setDashboardData] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // PWA Update hook
  const { updateAvailable, applyUpdate, checkForUpdate } = usePWAUpdate();

  const menuItems = [
    { label: 'لوحة التحكم الرئيسية', icon: <DashboardIcon />, path: '/admin/dashboard' },
    { label: 'إدارة المشرفين', icon: <AdminsIcon />, path: '/admin/admins' },
    { label: 'إدارة المستخدمين', icon: <UsersIcon />, path: '/admin/users' },
    { label: 'إدارة المنتجات', icon: <ProductsIcon />, path: '/admin/products' },
    { label: 'الفئات والعلامات', icon: <CategoriesIcon />, path: '/admin/categories' },
    { label: 'إدارة الطلبات', icon: <OrdersIcon />, path: '/admin/orders' },
    { label: 'واجهة المجهز', icon: <ShippingIcon />, path: '/admin/packaging' },
    { label: 'إدارة متجر الهدايا', icon: <GiftShopIcon />, path: '/admin/giftshop' },
    { label: 'إدارة الكوبونات', icon: <CouponsIcon />, path: '/admin/coupons' },
    { label: 'إدارة البانرات', icon: <BannersIcon />, path: '/admin/banners' },
    { label: 'المنتجات المفقودة', icon: <MissingIcon />, path: '/admin/missing-products' },
    { label: 'التقارير والإحصائيات', icon: <StatsIcon />, path: '/admin/reports' },
    { label: 'نظام ERP', icon: <StoreIcon />, path: '/admin/erp', highlight: true }
  ];

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await adminAuthService.getDashboardStats();
      setDashboardData(data);
    } catch (error) {
      setError('فشل في تحميل بيانات لوحة التحكم');
      if (error.message.includes('authentication expired')) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    // Check authentication
    if (!adminAuthService.isAuthenticated() || !adminAuthService.isAdmin()) {
      navigate('/admin/login');
      return;
    }

    // Check if user is packaging admin and redirect to packaging page
    const currentUser = adminAuthService.getCurrentUser();
    if (currentUser && currentUser.role === 'packaging_admin') {
      navigate('/admin/packaging');
      return;
    }

    // Check if user is inventory admin and redirect to missing products page
    if (currentUser && currentUser.role === 'inventory_admin') {
      navigate('/admin/missing-products');
      return;
    }

    loadDashboardData();
  }, [navigate, loadDashboardData]);

  const handleLogout = () => {
    adminAuthService.logout();
    navigate('/admin/login');
  };

  const handleMenuClick = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  const currentUser = adminAuthService.getCurrentUser();

  return (
    <Box sx={{ flexGrow: 1, display: 'flex' }}>
      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
        sx={{ display: { xs: 'block', md: 'none' } }}
      >
        <Box sx={{ width: 280, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
            القائمة الرئيسية
          </Typography>
          <List>
            {menuItems.map((item, index) => (
              <ListItem
                button
                key={index}
                onClick={() => handleMenuClick(item.path)}
                sx={{
                  mb: 1,
                  borderRadius: 2,
                  '&:hover': { backgroundColor: 'rgba(30, 58, 138, 0.1)' }
                }}
              >
                <ListItemIcon sx={{ color: '#1e3a8a' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1 }}>
        {/* Admin Navigation Bar */}
        <AppBar position="static">
          <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
            <AdminIcon sx={{ mr: { xs: 1, sm: 2 }, fontSize: { xs: 20, sm: 24 } }} />
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
              {isMobile ? 'الصادق' : `لوحة إدارة الصادق - مرحباً ${dashboardData?.admin_info?.name || currentUser?.name}`}
            </Typography>
            
            {/* Desktop Menu */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
              <Button color="inherit" onClick={() => handleMenuClick('/admin/users')}>
                <UsersIcon sx={{ mr: 1 }} />
                المستخدمين
              </Button>
              <Button color="inherit" onClick={() => handleMenuClick('/admin/products')}>
                <ProductsIcon sx={{ mr: 1 }} />
                المنتجات
              </Button>
              <Button color="inherit" onClick={() => handleMenuClick('/admin/orders')}>
                <OrdersIcon sx={{ mr: 1 }} />
                الطلبات
              </Button>
              <Button color="inherit" onClick={() => handleMenuClick('/admin/giftshop')}>
                <GiftShopIcon sx={{ mr: 1 }} />
                الهدايا
              </Button>
            </Box>

            {/* Mobile Menu Button */}
            <IconButton
              color="inherit"
              onClick={toggleMobileMenu}
              sx={{ display: { xs: 'block', md: 'none' }, mr: 1 }}
            >
              <MenuIcon />
            </IconButton>

            {/* Update Button */}
            {updateAvailable && (
              <Tooltip title="تحديث متوفر! اضغط للتحديث">
                <IconButton 
                  color="inherit" 
                  onClick={applyUpdate}
                  sx={{ 
                    mr: 1,
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 }
                    }
                  }}
                >
                  <Badge color="error" variant="dot">
                    <UpdateIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}

            <IconButton color="inherit" onClick={handleLogout} title="تسجيل الخروج">
              <LogoutIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3, md: 4 }, mb: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
          {/* Main Sections Title */}
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: { xs: 2, sm: 3 }, color: '#1e3a8a', fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
            الأقسام الرئيسية
          </Typography>

          {/* Dashboard Cards Grid - Your Admin System Elements */}
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            {/* PWA Update Card - Show only when update is available */}
            {updateAvailable && (
              <Grid item xs={12} sm={12} md={12}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    borderRadius: { xs: 2, sm: 3 },
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: '2px solid #5a67d8',
                    animation: 'slideDown 0.5s ease',
                    '@keyframes slideDown': {
                      '0%': { opacity: 0, transform: 'translateY(-20px)' },
                      '100%': { opacity: 1, transform: 'translateY(0)' }
                    },
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: '0 12px 35px rgba(102, 126, 234, 0.4)'
                    }
                  }}
                  onClick={applyUpdate}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ 
                          width: { xs: 50, sm: 60 }, 
                          height: { xs: 50, sm: 60 }, 
                          backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                          borderRadius: 2, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          animation: 'rotate 3s linear infinite',
                          '@keyframes rotate': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' }
                          }
                        }}>
                          <UpdateIcon sx={{ fontSize: { xs: 28, sm: 35 }, color: 'white' }} />
                        </Box>
                        <Box>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                            🎉 تحديث جديد متوفر!
                          </Typography>
                          <Typography variant="body1" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, opacity: 0.95 }}>
                            إصدار جديد من النظام متاح الآن. اضغط هنا للتحديث والحصول على أحدث الميزات والتحسينات
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        variant="contained"
                        sx={{
                          backgroundColor: 'white',
                          color: '#667eea',
                          fontWeight: 'bold',
                          px: { xs: 2, sm: 4 },
                          py: { xs: 1, sm: 1.5 },
                          fontSize: { xs: '0.85rem', sm: '1rem' },
                          '&:hover': {
                            backgroundColor: '#f8f8f8',
                            transform: 'scale(1.05)'
                          },
                          display: { xs: 'none', sm: 'block' }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          applyUpdate();
                        }}
                      >
                        تحديث الآن
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Admin Management */}
            <Grid item xs={6} sm={6} md={4} lg={2}>
              <Card
                sx={{
                  height: { xs: 160, sm: 180, md: 200 },
                  cursor: 'pointer',
                  borderRadius: { xs: 2, sm: 3 },
                  transition: 'all 0.3s ease',
                  backgroundColor: 'white',
                  border: '1px solid #f0f0f0',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                    borderColor: '#4facfe'
                  }
                }}
                onClick={() => handleMenuClick('/admin/admins')}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Box>
                    <Box sx={{ 
                      width: { xs: 36, sm: 42, md: 50 }, 
                      height: { xs: 36, sm: 42, md: 50 }, 
                      backgroundColor: '#f8faff', 
                      borderRadius: { xs: 1.5, sm: 2 }, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      mb: { xs: 1, sm: 1.5, md: 2 }
                    }}>
                      <AdminsIcon sx={{ fontSize: { xs: 20, sm: 24, md: 28 }, color: '#4facfe' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: { xs: 0.5, sm: 0.75, md: 1 }, fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' } }}>
                      إدارة المشرفين
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem' }, lineHeight: 1.4, display: { xs: 'none', sm: 'block' } }}>
                      إضافة وإدارة حسابات المشرفين
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: { xs: 1, sm: 0 } }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4facfe', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                      5
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      {isMobile ? 'مشرف' : 'مشرف نشط'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* User Management */}
            <Grid item xs={6} sm={6} md={4} lg={2}>
              <Card
                sx={{
                  height: { xs: 160, sm: 180, md: 200 },
                  cursor: 'pointer',
                  borderRadius: { xs: 2, sm: 3 },
                  transition: 'all 0.3s ease',
                  backgroundColor: 'white',
                  border: '1px solid #f0f0f0',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                    borderColor: '#fa709a'
                  }
                }}
                onClick={() => handleMenuClick('/admin/users')}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Box>
                    <Box sx={{ 
                      width: { xs: 36, sm: 42, md: 50 }, 
                      height: { xs: 36, sm: 42, md: 50 }, 
                      backgroundColor: '#fef7f7', 
                      borderRadius: { xs: 1.5, sm: 2 }, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      mb: { xs: 1, sm: 1.5, md: 2 }
                    }}>
                      <UsersIcon sx={{ fontSize: { xs: 20, sm: 24, md: 28 }, color: '#fa709a' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: { xs: 0.5, sm: 0.75, md: 1 }, fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' } }}>
                      إدارة العملاء
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem' }, lineHeight: 1.4, display: { xs: 'none', sm: 'block' } }}>
                      عرض وإدارة حسابات العملاء
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: { xs: 1, sm: 0 } }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#fa709a', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                      125
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      {isMobile ? 'عميل' : 'عميل مسجل'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Product Management */}
            <Grid item xs={6} sm={6} md={4} lg={2}>
              <Card
                sx={{
                  height: { xs: 160, sm: 180, md: 200 },
                  cursor: 'pointer',
                  borderRadius: { xs: 2, sm: 3 },
                  transition: 'all 0.3s ease',
                  backgroundColor: 'white',
                  border: '1px solid #f0f0f0',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                    borderColor: '#667eea'
                  }
                }}
                onClick={() => handleMenuClick('/admin/products')}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Box>
                    <Box sx={{ 
                      width: { xs: 36, sm: 42, md: 50 }, 
                      height: { xs: 36, sm: 42, md: 50 }, 
                      backgroundColor: '#f8f9ff', 
                      borderRadius: { xs: 1.5, sm: 2 }, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      mb: { xs: 1, sm: 1.5, md: 2 }
                    }}>
                      <ProductsIcon sx={{ fontSize: { xs: 20, sm: 24, md: 28 }, color: '#667eea' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: { xs: 0.5, sm: 0.75, md: 1 }, fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' } }}>
                      إدارة المنتجات
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem' }, lineHeight: 1.4, display: { xs: 'none', sm: 'block' } }}>
                      إضافة وتحرير منتجات المتجر
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: { xs: 1, sm: 0 } }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#667eea', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                      48
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      {isMobile ? 'منتج' : 'منتج نشط'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Order Management */}
            <Grid item xs={6} sm={6} md={4} lg={2}>
              <Card
                sx={{
                  height: { xs: 160, sm: 180, md: 200 },
                  cursor: 'pointer',
                  borderRadius: { xs: 2, sm: 3 },
                  transition: 'all 0.3s ease',
                  backgroundColor: 'white',
                  border: '1px solid #f0f0f0',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                    borderColor: '#ff9800'
                  }
                }}
                onClick={() => handleMenuClick('/admin/orders')}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Box>
                    <Box sx={{ 
                      width: { xs: 36, sm: 42, md: 50 }, 
                      height: { xs: 36, sm: 42, md: 50 }, 
                      backgroundColor: '#fff9f5', 
                      borderRadius: { xs: 1.5, sm: 2 }, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      mb: { xs: 1, sm: 1.5, md: 2 },
                      position: 'relative'
                    }}>
                      <OrdersIcon sx={{ fontSize: { xs: 20, sm: 24, md: 28 }, color: '#ff9800' }} />
                      <Box sx={{
                        position: 'absolute',
                        top: -5,
                        right: -5,
                        backgroundColor: '#ff4444',
                        borderRadius: '50%',
                        width: { xs: 16, sm: 20 },
                        height: { xs: 16, sm: 20 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Typography variant="caption" sx={{ color: 'white', fontSize: { xs: '8px', sm: '10px' }, fontWeight: 'bold' }}>
                          جديد
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: { xs: 0.5, sm: 0.75, md: 1 }, fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' } }}>
                      إدارة الطلبات
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem' }, lineHeight: 1.4, display: { xs: 'none', sm: 'block' } }}>
                      متابعة ومعالجة طلبات العملاء
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: { xs: 1, sm: 0 } }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ff9800', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                      12
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      {isMobile ? 'جديد' : 'طلب جديد'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Packaging Management */}
            <Grid item xs={6} sm={6} md={4} lg={2}>
              <Card
                sx={{
                  height: { xs: 160, sm: 180, md: 200 },
                  cursor: 'pointer',
                  borderRadius: { xs: 2, sm: 3 },
                  transition: 'all 0.3s ease',
                  backgroundColor: 'white',
                  border: '1px solid #f0f0f0',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                    borderColor: '#10b981'
                  }
                }}
                onClick={() => handleMenuClick('/admin/packaging')}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Box>
                    <Box sx={{ 
                      width: { xs: 36, sm: 42, md: 50 }, 
                      height: { xs: 36, sm: 42, md: 50 }, 
                      backgroundColor: '#f0fdf4', 
                      borderRadius: { xs: 1.5, sm: 2 }, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      mb: { xs: 1, sm: 1.5, md: 2 }
                    }}>
                      <ShippingIcon sx={{ fontSize: { xs: 20, sm: 24, md: 28 }, color: '#10b981' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: { xs: 0.5, sm: 0.75, md: 1 }, fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' } }}>
                      {isMobile ? 'المجهز' : 'واجهة المجهز'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem' }, lineHeight: 1.4, display: { xs: 'none', sm: 'block' } }}>
                      إدارة التعبئة والتغليف
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: { xs: 1, sm: 0 } }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#10b981' }}>
                      <ShippingIcon sx={{ fontSize: { xs: 24, sm: 28, md: 32 } }} />
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      للتجهيز
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Gift Shop Management */}
            <Grid item xs={6} sm={6} md={4} lg={2}>
              <Card
                sx={{
                  height: { xs: 160, sm: 180, md: 200 },
                  cursor: 'pointer',
                  borderRadius: { xs: 2, sm: 3 },
                  transition: 'all 0.3s ease',
                  backgroundColor: 'white',
                  border: '1px solid #f0f0f0',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                    borderColor: '#ff6b6b'
                  }
                }}
                onClick={() => handleMenuClick('/admin/giftshop')}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Box>
                    <Box sx={{ 
                      width: { xs: 36, sm: 42, md: 50 }, 
                      height: { xs: 36, sm: 42, md: 50 }, 
                      backgroundColor: '#fff5f5', 
                      borderRadius: { xs: 1.5, sm: 2 }, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      mb: { xs: 1, sm: 1.5, md: 2 }
                    }}>
                      <GiftShopIcon sx={{ fontSize: { xs: 20, sm: 24, md: 28 }, color: '#ff6b6b' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: { xs: 0.5, sm: 0.75, md: 1 }, fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' } }}>
                      {isMobile ? 'متجر الهدايا' : 'إدارة متجر الهدايا'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem' }, lineHeight: 1.4, display: { xs: 'none', sm: 'block' } }}>
                      إدارة منتجات وطلبات الهدايا
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: { xs: 1, sm: 0 } }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ff6b6b', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                      24
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      {isMobile ? 'هدية' : 'منتج هدايا'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Categories Management */}
            <Grid item xs={6} sm={6} md={4} lg={2}>
              <Card
                sx={{
                  height: { xs: 160, sm: 180, md: 200 },
                  cursor: 'pointer',
                  borderRadius: { xs: 2, sm: 3 },
                  transition: 'all 0.3s ease',
                  backgroundColor: 'white',
                  border: '1px solid #f0f0f0',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                    borderColor: '#52c41a'
                  }
                }}
                onClick={() => handleMenuClick('/admin/categories')}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Box>
                    <Box sx={{ 
                      width: { xs: 36, sm: 42, md: 50 }, 
                      height: { xs: 36, sm: 42, md: 50 }, 
                      backgroundColor: '#f6ffed', 
                      borderRadius: { xs: 1.5, sm: 2 }, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      mb: { xs: 1, sm: 1.5, md: 2 }
                    }}>
                      <CategoriesIcon sx={{ fontSize: { xs: 20, sm: 24, md: 28 }, color: '#52c41a' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: { xs: 0.5, sm: 0.75, md: 1 }, fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' } }}>
                      {isMobile ? 'الفئات' : 'الفئات والعلامات'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem' }, lineHeight: 1.4, display: { xs: 'none', sm: 'block' } }}>
                      إدارة تصنيفات المنتجات
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: { xs: 1, sm: 0 } }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#52c41a', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                      8
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      {isMobile ? 'فئة' : 'فئة نشطة'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Coupon Management */}
            <Grid item xs={6} sm={6} md={4} lg={2}>
              <Card
                sx={{
                  height: { xs: 160, sm: 180, md: 200 },
                  cursor: 'pointer',
                  borderRadius: { xs: 2, sm: 3 },
                  transition: 'all 0.3s ease',
                  backgroundColor: 'white',
                  border: '1px solid #f0f0f0',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                    borderColor: '#722ed1'
                  }
                }}
                onClick={() => handleMenuClick('/admin/coupons')}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Box>
                    <Box sx={{ 
                      width: { xs: 36, sm: 42, md: 50 }, 
                      height: { xs: 36, sm: 42, md: 50 }, 
                      backgroundColor: '#f9f0ff', 
                      borderRadius: { xs: 1.5, sm: 2 }, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      mb: { xs: 1, sm: 1.5, md: 2 }
                    }}>
                      <CouponsIcon sx={{ fontSize: { xs: 20, sm: 24, md: 28 }, color: '#722ed1' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: { xs: 0.5, sm: 0.75, md: 1 }, fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' } }}>
                      {isMobile ? 'الكوبونات' : 'إدارة الكوبونات'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem' }, lineHeight: 1.4, display: { xs: 'none', sm: 'block' } }}>
                      إنشاء وإدارة كوبونات الخصم
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: { xs: 1, sm: 0 } }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#722ed1', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                      15
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      {isMobile ? 'كوبون' : 'كوبون نشط'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Missing Products */}
            <Grid item xs={6} sm={6} md={4} lg={2}>
              <Card
                sx={{
                  height: { xs: 160, sm: 180, md: 200 },
                  cursor: 'pointer',
                  borderRadius: { xs: 2, sm: 3 },
                  transition: 'all 0.3s ease',
                  backgroundColor: 'white',
                  border: '1px solid #f0f0f0',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                    borderColor: '#ff4d4f'
                  }
                }}
                onClick={() => handleMenuClick('/admin/missing-products')}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Box>
                    <Box sx={{ 
                      width: { xs: 36, sm: 42, md: 50 }, 
                      height: { xs: 36, sm: 42, md: 50 }, 
                      backgroundColor: '#fff1f0', 
                      borderRadius: { xs: 1.5, sm: 2 }, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      mb: { xs: 1, sm: 1.5, md: 2 }
                    }}>
                      <MissingIcon sx={{ fontSize: { xs: 20, sm: 24, md: 28 }, color: '#ff4d4f' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: { xs: 0.5, sm: 0.75, md: 1 }, fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' } }}>
                      {isMobile ? 'المنتجات المفقودة' : 'المنتجات المفقودة'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem' }, lineHeight: 1.4, display: { xs: 'none', sm: 'block' } }}>
                      متابعة المنتجات غير المتوفرة
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: { xs: 1, sm: 0 } }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ff4d4f', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                      0
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      {isMobile ? 'منتج' : 'منتج مفقود'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Reports and Analytics */}
            <Grid item xs={6} sm={6} md={4} lg={2}>
              <Card
                sx={{
                  height: { xs: 160, sm: 180, md: 200 },
                  cursor: 'pointer',
                  borderRadius: { xs: 2, sm: 3 },
                  transition: 'all 0.3s ease',
                  backgroundColor: 'white',
                  border: '1px solid #f0f0f0',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                    borderColor: '#66a6ff'
                  }
                }}
                onClick={() => handleMenuClick('/admin/reports')}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Box>
                    <Box sx={{ 
                      width: { xs: 36, sm: 42, md: 50 }, 
                      height: { xs: 36, sm: 42, md: 50 }, 
                      backgroundColor: '#f0f7ff', 
                      borderRadius: { xs: 1.5, sm: 2 }, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      mb: { xs: 1, sm: 1.5, md: 2 }
                    }}>
                      <StatsIcon sx={{ fontSize: { xs: 20, sm: 24, md: 28 }, color: '#66a6ff' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: { xs: 0.5, sm: 0.75, md: 1 }, fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' } }}>
                      {isMobile ? 'التقارير' : 'التقارير والإحصائيات'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem' }, lineHeight: 1.4, display: { xs: 'none', sm: 'block' } }}>
                      عرض تقارير المبيعات والأرباح
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: { xs: 1, sm: 0 } }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#66a6ff', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                      ₪2.5K
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      {isMobile ? 'اليوم' : 'مبيعات اليوم'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Banner Management */}
            <Grid item xs={6} sm={6} md={4} lg={2}>
              <Card
                sx={{
                  height: { xs: 160, sm: 180, md: 200 },
                  cursor: 'pointer',
                  borderRadius: { xs: 2, sm: 3 },
                  transition: 'all 0.3s ease',
                  backgroundColor: 'white',
                  border: '1px solid #f0f0f0',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                    borderColor: '#eb2f96'
                  }
                }}
                onClick={() => handleMenuClick('/admin/banners')}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Box>
                    <Box sx={{ 
                      width: { xs: 36, sm: 42, md: 50 }, 
                      height: { xs: 36, sm: 42, md: 50 }, 
                      backgroundColor: '#fff0f6', 
                      borderRadius: { xs: 1.5, sm: 2 }, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      mb: { xs: 1, sm: 1.5, md: 2 }
                    }}>
                      <BannersIcon sx={{ fontSize: { xs: 20, sm: 24, md: 28 }, color: '#eb2f96' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: { xs: 0.5, sm: 0.75, md: 1 }, fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' } }}>
                      {isMobile ? 'البانرات' : 'إدارة البانرات'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem' }, lineHeight: 1.4, display: { xs: 'none', sm: 'block' } }}>
                      إدارة بانرات الصفحة الرئيسية
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: { xs: 1, sm: 0 } }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#eb2f96', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                      5
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      {isMobile ? 'بانر' : 'بانر نشط'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 4 }}>
              {error}
              <Button onClick={loadDashboardData} sx={{ ml: 2 }}>إعادة المحاولة</Button>
            </Alert>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
